import { defineStore } from 'pinia'
import sortBy from 'lodash/sortBy'
import cloneDeep from 'lodash/cloneDeep'
import { FolderApi, DocumentApi } from '../test-api/index.ts'
import handleApiError from '../utils/handleApiError'

// ids which has been loaded in folders cache
let loadedHashTable = { bc: Object.create(null) }
const folderApi = new FolderApi({}, '/api/v2')
const documentApi = new DocumentApi({}, '/api/v2')

// initial state
const state = () => ({
  folders: { bc: [] },
  documents: { bc: [] },
  selectedItem: null, // current selected object
  selectedDomain: null, // current selected domain
  loading: true,
  order: 'rank', // rank, name
  bcId: 'bc', // bc for current bc or <id> of brandcloud
  localBcId: null,
  resetNeeded: false, // reset on next folder
})

// functionName: (state, getters, rootState) => {}
const getters = {
  listChildrenDocuments() {
    const hashTable = Object.create(null)
    this.documents[this.bcId]?.forEach((document) => {
      if (document.deleted)
        return

      const parentId = document.bc_folder_id ? document.bc_folder_id : 0

      if (!hashTable[parentId]) hashTable[parentId] = []

      hashTable[parentId].push(document)
    })
    return hashTable
  },
  listChildrenFolders() {
    const hashTable = Object.create(null)
    this.folders[this.bcId]?.forEach((folder) => {
      if (folder.deleted)
        return

      const parentId = folder.parent__bc_folder_id
        ? folder.parent__bc_folder_id
        : 0

      if (!hashTable[parentId]) hashTable[parentId] = []

      hashTable[parentId].push(folder)
    })
    return hashTable
  },
  listChildren() {
    const hashTable = { ...this.listChildrenFolders }

    Object.keys(this.listChildrenDocuments).forEach((parentId) => {
      if (!hashTable[parentId]) hashTable[parentId] = []

      hashTable[parentId] = hashTable[parentId].concat(
        this.listChildrenDocuments[parentId]
      )
    })

    Object.keys(hashTable).forEach((parentId) => {
      hashTable[parentId] = sortBy(hashTable[parentId], [this.order])
    })

    return hashTable
  },
  listParents() {
    const hashTable = this.hashTable
    return (childId) => {
      if (!childId || !hashTable[childId]) return []

      const parents = []
      let parentId = hashTable[childId].parent__bc_folder_id
      while (parentId) {
        parents.push(hashTable[parentId])
        parentId = hashTable[parentId].parent__bc_folder_id
      }
      return parents.reverse()
    }
  },
  hashTable() {
    const hashTable = Object.create(null)
    this.folders[this.bcId]?.forEach((folder) => {
      if (folder.deleted)
        return

      hashTable[folder.id] = folder
      hashTable[folder.id].children = []
    })
    return hashTable
  },
  hashTableDocuments() {
    const hashTable = Object.create(null)
    this.documents[this.bcId]?.forEach((document) => {
      if (document.deleted)
        return

      hashTable[document.id] = document
    })
    return hashTable
  },
  treeFolders() {
    const dataTree = []
    const hashTable = cloneDeep(this.hashTable)
    this.folders[this.bcId]?.forEach((folder) => {
      if (folder.deleted)
        return

      if (folder.parent__bc_folder_id)
        hashTable[folder.parent__bc_folder_id]?.children.push(
          hashTable[folder.id]
        )
      else dataTree.push(hashTable[folder.id])
    })
    return dataTree
  },
  tree() {
    let dataTree = []
    const hashTable = cloneDeep(this.hashTable)
    this.folders[this.bcId]?.forEach((folder) => {
      if (folder.deleted)
        return

      if (folder.parent__bc_folder_id)
        hashTable[folder.parent__bc_folder_id]?.children.push(
          hashTable[folder.id]
        )
      else dataTree.push(hashTable[folder.id])
    })
    this.documents[this.bcId]?.forEach((document) => {
      if (document.deleted)
        return

      if (document.bc_folder_id && !hashTable[document.bc_folder_id]) return

      if (document.bc_folder_id)
        hashTable[document.bc_folder_id].children.push(document)
      else dataTree.push(document)
    })
    // sort children
    this.folders[this.bcId]?.forEach((folder) => {
      if (folder.deleted)
        return

      if (hashTable[folder.id].children.length) {
        hashTable[folder.id].children = sortBy(hashTable[folder.id].children, [
          this.order
        ])
      }
    })

    return sortBy(dataTree, [this.order])
  }
}

// functionName ({ commit, state, dispatch }, param, ...) {}
const actions = {
  async loadFolder(id) {
    this.bcId = this.selectedDomain && this.selectedDomain.id !== this.localBcId ? this.selectedDomain.id : 'bc'

    if (!loadedHashTable[this.bcId]) {
      loadedHashTable[this.bcId] = Object.create(null)
      this.folders[this.bcId] = []
      this.documents[this.bcId] = []
    }

    if (this.resetNeeded) {
      this.resetNeeded = false
      this.reset()
    }

    if (loadedHashTable[this.bcId][id]) return

    this.loading = true
    loadedHashTable[this.bcId][id] = true
    let foldersListResponse, documentsListResponse

    foldersListResponse = handleApiError(() => folderApi.folderList(
      id || undefined,
      this.selectedDomain && this.selectedDomain.id !== this.localBcId ? this.selectedDomain.id : undefined
    ))
    
    documentsListResponse = handleApiError(() => documentApi.documentList(
      id || undefined,
      this.selectedDomain && this.selectedDomain.id !== this.localBcId ? this.selectedDomain.id : undefined
    ))

    foldersListResponse = await foldersListResponse
    documentsListResponse = await documentsListResponse

    this.loadFolders(foldersListResponse.data, documentsListResponse.data)
  },
  loadFolders(folders, documents) {
    folders.forEach((folder) => {
      if (folder.parent__bc_folder_id)
        loadedHashTable[this.bcId][folder.id] = true
    })
    this.folders[this.bcId] = [].concat(this.folders[this.bcId], folders)
    this.documents[this.bcId] = [].concat(this.documents[this.bcId], documents)
    this.loading = false
  },
  setResetNeeded() {
    this.resetNeeded = true
  },
  toggleOrder() {
    if (this.order === 'rank') {
      this.order = 'name'
    } else {
      this.order = 'rank'
    }
  },
  reset() {
    this.folders = { bc: [] }
    this.documents = { bc: [] }
    this.selectedItem = null // current folder object
    this.loading = true

    loadedHashTable = {
      bc: Object.create(null),
    }
  }
}

export default defineStore({
  id: 'bcMoveCopy',
  state,
  getters,
  actions
})
